import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { config } from '../config';
import { app } from '../firebase';

/**
 * OS-level notifications for the STANDALONE console (`/comms`), the two legs
 * the in-app toast cannot cover:
 *
 *  1. Foreground-but-hidden — the tab is open behind another window. The page
 *     is alive, so `new Notification(...)` from the socket event is enough.
 *  2. Closed — no tab at all. Only a service worker woken by a push can fire,
 *     so the console registers `public/firebase-messaging-sw.js` and hands its
 *     FCM token to the CRM (`POST /notifications/devices`).
 *
 *     It used to subscribe that token to the tenant's admin TOPIC instead. A
 *     topic cannot be addressed, and the `notification` block is OS-rendered, so
 *     that delivered a lock-screen banner — message preview included — to every
 *     admin device regardless of which inboxes the agent could actually see.
 *     Registering the token means the server pushes to people, not to a tenant.
 *
 * The EMBEDDED console (`/embed`) does none of this: the dispatch dashboard
 * owns the FCM registration for its own origin and would double-notify.
 *
 * Everything degrades to silence. A blocked permission, a browser without
 * push, a service-worker registration failure or a dead subscribe endpoint all
 * leave the in-app toasts working exactly as before.
 */

/** VAPID public keys for the two dispatch Firebase projects (public by design). */
const VAPID_KEYS = {
  uat: 'BPUyc9GhvipxCGGIk8pb75V2BQ28hHsiiGtBOdCDQ_H4E7EMhwHddA_yL5YnLIgAOjGBxdiZ635Fbw8aRmCnDbg',
  prod: 'BMVM00nmKBdYwJd6oMvmezadvUe_ltwHTlcTjKqgQGKNQuEgeAgd4ZyLZdsCdozD_RlDHfDm-TcOL5dtxsmBhaQ',
} as const;

export type PermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export function notificationPermission(): PermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as PermissionState;
}

/**
 * Must be called from a user gesture — Safari rejects it otherwise, and
 * Chrome ignores prompts on pages the user has never interacted with.
 */
export async function requestNotificationPermission(): Promise<PermissionState> {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return (await Notification.requestPermission()) as PermissionState;
  } catch {
    return notificationPermission();
  }
}

/**
 * Raise an OS banner. `tag` collapses repeats for one conversation so a busy
 * thread leaves one banner, not twelve; clicking focuses this window.
 */
export function showSystemNotification({
  title,
  body,
  tag,
  onClick,
}: {
  title: string;
  body: string;
  tag?: string;
  onClick?: () => void;
}): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(title, {
      body,
      tag,
      icon: `${import.meta.env.BASE_URL}favicon.svg`,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      onClick?.();
    };
  } catch {
    // Android Chrome throws on the constructor (it demands a service worker);
    // the push leg covers that platform anyway.
  }
}

let pushRegistered: string | null = null;
// The token we registered, kept so sign-out can release it without asking FCM
// for it again — by then the Firebase session may already be gone.
let registeredToken: string | null = null;

/**
 * Register the background-push leg for `tenant`. Idempotent per tenant —
 * re-running it on every mount is free. Resolves to the FCM token on success
 * and null on any failure; nothing here is fatal.
 *
 * `register` must be the CRM api's method, not a bare fetch: the route needs the
 * agent's ID token and the tenant header, and answers 1034 without them.
 */
export async function registerBackgroundPush(
  tenant: string,
  register: (token: string, platform?: string) => Promise<unknown>,
): Promise<string | null> {
  if (pushRegistered === tenant) return null;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (notificationPermission() !== 'granted') return null;
  if (!(await isSupported().catch(() => false))) return null;

  pushRegistered = tenant;
  try {
    const registration = await navigator.serviceWorker.register(
      `${import.meta.env.BASE_URL}firebase-messaging-sw.js`,
      { scope: import.meta.env.BASE_URL },
    );
    const token = await getToken(getMessaging(app), {
      vapidKey: VAPID_KEYS[config.firebaseEnv],
      serviceWorkerRegistration: registration,
    });
    if (!token) {
      pushRegistered = null;
      return null;
    }
    // Registering the same token again just moves it to the current agent and
    // refreshes its last-seen — which is exactly what should happen when a
    // second person signs in on a shared machine.
    await register(token, 'web');
    registeredToken = token;
    return token;
  } catch {
    // Retry on the next mount rather than wedging this tenant forever.
    pushRegistered = null;
    return null;
  }
}

/**
 * Release this browser's push registration. MUST run before the Firebase
 * sign-out, since the call needs the agent's ID token.
 *
 * Without it a shared dispatch machine keeps pushing the signed-out agent's
 * conversations to that browser until somebody else signs in and re-registers
 * the same token — which may be never, if the next person declines push.
 *
 * Best effort in every direction: no token, a failed call and a browser without
 * push all resolve quietly. A sign-out must never be blocked by a push cleanup.
 */
export async function releaseBackgroundPush(
  unregister: (token: string) => Promise<unknown>,
): Promise<void> {
  const token = registeredToken;
  pushRegistered = null;
  registeredToken = null;
  if (!token) return;
  await unregister(token).catch(() => {});
}
